import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X } from "lucide-react";
import { isStandaloneMode } from "@/lib/pwa";
import {
  pushCapability,
  currentPushPermission,
  isCurrentlySubscribed,
  subscribeToPush,
} from "@/lib/pushNotifications";

const PROMPTED_KEY = "summit_air_push_prompted";

/**
 * One-time (per browser install) prompt asking the user to enable push
 * notifications after they've installed the app. Only shown when:
 *   - browser is running in standalone / home-screen mode (so iOS PWA path works),
 *   - push is supported on this browser,
 *   - permission has not yet been decided (default),
 *   - the user is not already subscribed,
 *   - we haven't asked before (localStorage flag).
 */
export default function PostInstallPushPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isStandaloneMode()) return;
      if (pushCapability() !== "supported") return;
      if (currentPushPermission() !== "default") return;
      if (localStorage.getItem(PROMPTED_KEY)) return;
      try {
        if (await isCurrentlySubscribed()) return;
      } catch { /* ignore */ }
      setVisible(true);
    })();
  }, []);

  const dismiss = () => {
    localStorage.setItem(PROMPTED_KEY, String(Date.now()));
    setVisible(false);
  };
  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifications enabled — you&apos;ll get a phone alert when a timesheet is submitted.");
      dismiss();
    } catch (e) {
      toast.error(e?.message || "Couldn't enable notifications");
      dismiss();  // don't nag the user again if they explicitly denied
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;
  return (
    <div
      className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 flex items-start gap-3"
      data-testid="post-install-push-prompt"
    >
      <div className="w-8 h-8 shrink-0 rounded-md bg-emerald-100 flex items-center justify-center">
        <Bell className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-sm font-medium tracking-tight">Turn on approval notifications?</div>
        <p className="mt-0.5 text-xs text-emerald-900/80">
          Get a phone alert when someone submits their timesheet for you to approve. You can toggle
          this any time under Profile.
        </p>
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-700 text-white px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-emerald-800 disabled:opacity-50"
          data-testid="post-install-push-enable"
        >
          <Bell className="w-3.5 h-3.5" /> Enable
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 p-1 -m-1 rounded-md hover:bg-emerald-100"
        aria-label="Dismiss"
        data-testid="post-install-push-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
